import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  MARKETING_SME_PLANS,
  MARKETING_PLAN_LABEL,
  MARKETING_RECOMMENDED_PLAN,
  type MarketingPlanDescriptor,
  type MarketingPlanId,
} from '@/lib/marketing/planCatalog';
import { cn } from '@/lib/utils';

function formatNum(n: number, locale: string): string {
  return n >= 1000 ? n.toLocaleString(locale) : String(n);
}

function signupHref(planId: MarketingPlanId): string {
  if (planId === 'freemium_trial') return '/signup';
  return `/signup?plan=${planId}`;
}

function MarketingPlanCard({
  plan,
  recommended,
}: {
  plan: MarketingPlanDescriptor;
  recommended: boolean;
}) {
  const { t, language } = useLanguage();
  const locale = language === 'it' ? 'it-IT' : 'en-GB';
  const isTrial = plan.planId === 'freemium_trial';
  const label = MARKETING_PLAN_LABEL[plan.planId];

  const priceLine = isTrial ? (
    <>
      <span className="text-3xl font-bold">{t('subscription.card.priceTrial')}</span>
      <span className="text-sm text-muted-foreground">
        {t('subscription.card.priceTrialPeriod', { days: plan.trialDays ?? 7 })}
      </span>
    </>
  ) : (
    <>
      <span className="text-3xl font-bold">€{plan.priceMonthly}</span>
      <span className="text-sm text-muted-foreground">{t('subscription.perMonth')}</span>
    </>
  );

  const ctaLabel = isTrial
    ? t('marketing.pricing.ctaStartTrial')
    : t('marketing.pricing.ctaSubscribe');

  return (
    <Card
      role="article"
      aria-label={label}
      className={cn(
        'relative p-5 flex flex-col gap-4 h-full',
        recommended && 'border-brand-accent/50 ring-1 ring-brand-accent/25',
      )}
    >
      {recommended && (
        <Badge className="absolute -top-2 left-4 bg-brand-accent text-white hover:bg-brand-accent">
          {t('subscription.card.recommended')}
        </Badge>
      )}

      <h3 className="text-base font-semibold pt-1">{label}</h3>

      <div className="flex items-baseline gap-1 flex-wrap">{priceLine}</div>

      <ul className="text-xs space-y-1.5 text-muted-foreground flex-1">
        <li>· {t('subscription.limits.seats', { n: formatNum(plan.limits.seats, locale) })}</li>
        <li>· {t('marketing.limits.positions', { n: formatNum(plan.limits.positions, locale) })}</li>
        <li>
          ·{' '}
          {isTrial
            ? t('subscription.limits.candidatesNoMonth', { n: formatNum(plan.limits.candidates, locale) })
            : t('subscription.limits.candidates', { n: formatNum(plan.limits.candidates, locale) })}
        </li>
        <li>
          ·{' '}
          {isTrial
            ? t('subscription.limits.aiCompany', { n: formatNum(plan.limits.aiCompany, locale) })
            : t('subscription.limits.aiCompanyMonthly', { n: formatNum(plan.limits.aiCompany, locale) })}
        </li>
      </ul>

      <Button
        size="sm"
        className={cn('w-full', recommended && 'bg-brand-accent hover:bg-brand-accent/90')}
        variant={recommended ? 'default' : 'outline'}
        asChild
      >
        <Link to={signupHref(plan.planId)}>{ctaLabel}</Link>
      </Button>
    </Card>
  );
}

/** Public pricing grid — mirrors in-app SubscriptionTab plan cards (guest CTAs → signup). */
export function MarketingPlanCards() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {MARKETING_SME_PLANS.map((plan) => (
        <MarketingPlanCard
          key={plan.planId}
          plan={plan}
          recommended={plan.planId === MARKETING_RECOMMENDED_PLAN}
        />
      ))}
    </div>
  );
}
